import { format } from 'date-fns';
import { css } from '@emotion/react';
import { Tooltip } from '@material-ui/core';
import AccountCircle from '@material-ui/icons/AccountCircle';
import DeviceHub from '@material-ui/icons/DeviceHub';
import SettingsEthernet from '@material-ui/icons/SettingsEthernet';
import { renderActivityLog } from '@onward-delivery/core';
import { colors } from '@/styles';
import { sansSerif } from '@/styles/typography';
import { useMemo } from 'react';
import RowMenu from '@/components/ExpandableGrid/RowMenu';
import { getActivityLogSource } from './source';

const ENTITY_LABELS = {
    ROUTE: 'Route',
    CLIENT: 'Client',
    EXCEPTION: 'Exception',
    MESSAGE: 'Message',
    NOTE: 'Note',
    MANIFEST: 'Manifest',
    ORDER: 'Order',
    ITEM: 'Item',
};

export const getEntityLabel = (log) => ENTITY_LABELS[log?.entity_type] || log?.entity_type || '—';

// Scheduling rows carry the reschedule reason, and are the only rows whose note/reason can be edited.
export const canEditRescheduleReason = (log) => log?.log_type === 'SCHEDULING';

const Text = ({ children }) => (
    <span
        css={css`
            ${sansSerif}
        `}
    >
        {children}
    </span>
);

const SOURCE_ICON = {
    user: AccountCircle,
    integration: SettingsEthernet,
    channel: DeviceHub,
};

const SOURCE_KIND_LABEL = {
    user: 'User',
    integration: 'Integration',
    channel: 'Channel',
};

const SourceCell = ({ value }) => {
    const Icon = SOURCE_ICON[value?.kind];
    const kindLabel = SOURCE_KIND_LABEL[value?.kind];
    if (!Icon || !kindLabel || !value?.label) {
        return null;
    }

    return (
        <span
            css={css`
                display: inline-flex;
                align-items: center;
                gap: 6px;
                max-width: 100%;
            `}
        >
            <Tooltip title={kindLabel}>
                <span style={{ display: 'inline-flex', flex: '0 0 auto' }}>
                    <Icon
                        aria-hidden="true"
                        data-testid={`activity-log-source-${value.kind}`}
                        style={{ fontSize: 16, color: colors.greys.secondary }}
                    />
                </span>
            </Tooltip>
            {value.labelTooltip ? (
                <Tooltip title={value.labelTooltip}>
                    <span
                        css={css`
                            border-bottom: 1px dotted ${colors.greys.secondary};
                            cursor: default;
                        `}
                    >
                        {value.label}
                    </span>
                </Tooltip>
            ) : (
                <span>{value.label}</span>
            )}
        </span>
    );
};

// Shared activity-log columns (orders + manifest). `getRowActions(log)` supplies the 3-dot menu
// items; the menu column is always rendered and stays empty where a row has no actions.
// `tz` is the IANA zone used to format typed timestamp/window values (renderActivityLog defaults
// to America/New_York when omitted); DATE-shaped values render the same regardless of tz.
export const useColumns = (compact = false, getRowActions = () => [], tz) => {
    return useMemo(() => {
        return [
            {
                Header: 'Activity Log',
                id: 'data-columns',
                columns: [
                    {
                        Header: 'Date',
                        id: 'timestamp',
                        width: 150,
                        disableSortBy: true,
                        accessor: (log) => log.timestamp,
                        Cell: ({ value }) => <Text>{value ? format(new Date(value), 'MMM d, yyyy h:mm a') : '-'}</Text>,
                    },
                    {
                        Header: 'Description',
                        id: 'log_message',
                        width: compact ? 260 : 340,
                        disableSortBy: true,
                        accessor: (log) => renderActivityLog(log, { tz })?.message ?? log.log_message,
                        Cell: ({ value }) => <Text>{value}</Text>,
                    },
                    {
                        Header: 'Details',
                        id: 'log_detail',
                        width: compact ? 200 : 260,
                        disableSortBy: true,
                        accessor: (log) => renderActivityLog(log, { tz })?.detail ?? log.log_detail,
                        Cell: ({ value }) => (
                            <span
                                css={css`
                                    ${sansSerif}
                                    color: ${colors.greys.secondary};
                                `}
                            >
                                {value || '—'}
                            </span>
                        ),
                    },
                    {
                        Header: 'Entity',
                        id: 'entity_type',
                        width: 130,
                        disableSortBy: true,
                        accessor: (log) => getEntityLabel(log),
                        Cell: ({ value }) => <Text>{value}</Text>,
                    },
                    {
                        Header: 'Source',
                        id: 'source',
                        width: 150,
                        disableSortBy: true,
                        accessor: (log) => getActivityLogSource(log),
                        Cell: SourceCell,
                    },
                    {
                        Header: '',
                        id: 'actions',
                        width: 60,
                        disableSortBy: true,
                        accessor: (log) => log,
                        Cell: ({ value: log }) => <RowMenu actions={getRowActions(log)} />,
                    },
                ],
            },
        ];
    }, [compact, getRowActions, tz]);
};
